import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

const BADGES = [
  // STARTER
  { id: 'first_trade', tier: 'starter', icon: '🎯', title: 'First Shot', desc: 'Log your first trade.', check: s => s.total >= 1 },
  { id: 'ten_trades', tier: 'starter', icon: '📓', title: 'Journal Habit', desc: 'Log 10 trades.', check: s => s.total >= 10 },
  { id: 'first_win', tier: 'starter', icon: '✓', title: 'First Win', desc: 'Win your first trade.', check: s => s.wins >= 1 },
  { id: 'first_loss_logged', tier: 'starter', icon: '🪨', title: 'Took the L', desc: 'Log a loss honestly. Discipline starts here.', check: s => s.losses >= 1 },

  // DISCIPLINE
  { id: 'checklist_5', tier: 'discipline', icon: '✅', title: 'Checklist Master', desc: 'Complete the 12-point checklist on 5 trades.', check: s => s.fullChecklist >= 5 },
  { id: 'checklist_25', tier: 'discipline', icon: '🛡️', title: 'Disciplined Trader', desc: 'Complete the checklist on 25 trades.', check: s => s.fullChecklist >= 25 },
  { id: 'no_revenge', tier: 'discipline', icon: '🧘', title: 'Calm Under Pressure', desc: 'Log 20 trades without any "Revenge" emotional state.', check: s => s.total >= 20 && s.revengeTrades === 0 },
  { id: 'bias_streak_5', tier: 'discipline', icon: '🧭', title: 'Bias Builder', desc: 'Log 5 daily biases.', check: s => s.biasCount >= 5 },

  // PERFORMANCE
  { id: 'win_streak_3', tier: 'performance', icon: '🔥', title: 'Hot Hand', desc: 'Win 3 trades in a row.', check: s => s.longestWinStreak >= 3 },
  { id: 'win_streak_5', tier: 'performance', icon: '🚀', title: 'On a Roll', desc: 'Win 5 trades in a row.', check: s => s.longestWinStreak >= 5 },
  { id: 'positive_r_10', tier: 'performance', icon: '📈', title: 'Edge Confirmed', desc: 'Reach +10R cumulative.', check: s => s.totalR >= 10 },
  { id: 'positive_r_50', tier: 'performance', icon: '💎', title: 'Diamond Hands', desc: 'Reach +50R cumulative.', check: s => s.totalR >= 50 },
  { id: 'big_win', tier: 'performance', icon: '🏆', title: 'Big Catch', desc: 'Hit a single trade at +5R or higher.', check: s => s.biggestWin >= 5 },
  { id: 'profit_factor', tier: 'performance', icon: '⚖️', title: 'Statistical Edge', desc: 'Reach a profit factor of 2.0+ with 20+ trades.', check: s => s.profitFactor >= 2 && s.total >= 20 },

  // CONSISTENCY
  { id: 'week_streak', tier: 'consistency', icon: '📅', title: 'Week Warrior', desc: 'Log a trade 5 days in a row.', check: s => s.dailyStreak >= 5 },
  { id: 'month_streak', tier: 'consistency', icon: '🗓️', title: 'Month Marathon', desc: 'Log trades for 20 different days.', check: s => s.uniqueDays >= 20 },
  { id: 'review_master', tier: 'consistency', icon: '📝', title: 'Notes Taker', desc: 'Add detailed notes to 15 trades.', check: s => s.notedTrades >= 15 },
  { id: 'tagged_master', tier: 'consistency', icon: '🏷️', title: 'Organized Mind', desc: 'Tag 10 trades with setups.', check: s => s.taggedTrades >= 10 },

  // MASTERY
  { id: 'hundred_trades', tier: 'mastery', icon: '💯', title: 'Centurion', desc: 'Log 100 trades.', check: s => s.total >= 100 },
  { id: 'expectancy_master', tier: 'mastery', icon: '🌟', title: 'Profitable Edge', desc: 'Maintain +0.5R expectancy over 50+ trades.', check: s => s.total >= 50 && s.expectancy >= 0.5 },
  { id: 'risk_master', tier: 'mastery', icon: '🛡️', title: 'Risk Guardian', desc: 'Log 50 trades with no single loss greater than -1.5R.', check: s => s.total >= 50 && s.maxLoss > -1.5 },
];

const TIER_NAMES = {
  starter: 'Starter',
  discipline: 'Discipline',
  performance: 'Performance',
  consistency: 'Consistency',
  mastery: 'Mastery'
};

export default function Achievements() {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [biases, setBiases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [tradesRes, biasRes] = await Promise.all([
      supabase.from('trades').select('*').eq('user_id', user.id).order('trade_date', { ascending: false }),
      supabase.from('daily_bias').select('id').eq('user_id', user.id)
    ]);
    setTrades(tradesRes.data || []);
    setBiases(biasRes.data || []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = useMemo(() => {
    const wins = trades.filter(t => t.outcome === 'Win');
    const losses = trades.filter(t => t.outcome === 'Loss');
    const fullChecklist = trades.filter(t => t.checklist_complete === 'Yes').length;
    const revengeTrades = trades.filter(t => t.emotional_state === 'Revenge').length;
    const notedTrades = trades.filter(t => t.notes && t.notes.length > 30).length;
    const taggedTrades = trades.filter(t => t.tags && t.tags.length > 0).length;

    const totalR = trades.reduce((s, t) => s + (parseFloat(t.r_multiple) || 0), 0);
    const rVals = trades.map(t => parseFloat(t.r_multiple) || 0);
    const biggestWin = rVals.length ? Math.max(...rVals, 0) : 0;
    const maxLoss = rVals.length ? Math.min(...rVals, 0) : 0;

    const avgWin = wins.length ? wins.reduce((s, t) => s + (parseFloat(t.r_multiple) || 0), 0) / wins.length : 0;
    const avgLoss = losses.length ? Math.abs(losses.reduce((s, t) => s + (parseFloat(t.r_multiple) || 0), 0) / losses.length) : 0;
    const profitFactor = avgLoss > 0 && losses.length > 0 ? (avgWin * wins.length) / (avgLoss * losses.length) : 0;
    const expectancy = trades.length > 0 ? totalR / trades.length : 0;

    // Longest win streak
    let longestWinStreak = 0, tmp = 0;
    const sortedByDate = [...trades].sort((a, b) => new Date(a.trade_date) - new Date(b.trade_date));
    sortedByDate.forEach(t => {
      if (t.outcome === 'Win') { tmp++; if (tmp > longestWinStreak) longestWinStreak = tmp; }
      else if (t.outcome === 'Loss') { tmp = 0; }
    });

    // Daily streak (consecutive days with at least one trade)
    const uniqueDates = [...new Set(trades.map(t => t.trade_date))].sort().reverse();
    const uniqueDays = uniqueDates.length;
    let dailyStreak = 0;
    if (uniqueDates.length > 0) {
      dailyStreak = 1;
      for (let i = 1; i < uniqueDates.length; i++) {
        const curr = new Date(uniqueDates[i-1]);
        const prev = new Date(uniqueDates[i]);
        const diffDays = (curr - prev) / 86400000;
        if (diffDays <= 3) dailyStreak++;  // weekends allowed (max gap of 3 days)
        else break;
      }
    }

    return {
      total: trades.length,
      wins: wins.length,
      losses: losses.length,
      fullChecklist,
      revengeTrades,
      notedTrades,
      taggedTrades,
      totalR,
      biggestWin,
      maxLoss,
      profitFactor,
      expectancy,
      longestWinStreak,
      dailyStreak,
      uniqueDays,
      biasCount: biases.length
    };
  }, [trades, biases]);

  const unlocked = BADGES.filter(b => b.check(stats));
  const unlockedIds = new Set(unlocked.map(b => b.id));
  const unlockedCount = unlocked.length;
  const totalCount = BADGES.length;
  const progressPct = (unlockedCount / totalCount) * 100;

  const tiers = ['starter', 'discipline', 'performance', 'consistency', 'mastery'];

  if (loading) return <div style={{textAlign:'center',padding:32,color:'var(--text-3)'}}>Loading achievements...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title"><em>Achievements</em></h1>
        <p className="page-subtitle">Unlock badges by trading with discipline and consistency</p>
      </div>

      {/* PROGRESS HEADER */}
      <div className="card mb-24" style={{background: 'linear-gradient(135deg, var(--bg-2), var(--bg-3))'}}>
        <div className="flex-between mb-12">
          <div>
            <div style={{fontFamily:'Cormorant Garamond, serif',fontSize:28,color:'var(--gold-2)',fontStyle:'italic'}}>
              {unlockedCount} / {totalCount} unlocked
            </div>
            <div style={{fontSize:13,color:'var(--text-3)',marginTop:4}}>
              {progressPct < 25 ? 'Just getting started — keep logging.' :
               progressPct < 50 ? 'Building solid habits.' :
               progressPct < 75 ? 'Now you\'re trading like a professional.' :
               'Elite-level discipline. Few traders reach this point.'}
            </div>
          </div>
          <div className="kpi-value" style={{fontSize:42}}>{Math.round(progressPct)}%</div>
        </div>
        <div className="progress-track" style={{height:8}}>
          <div className="progress-fill" style={{width:`${progressPct}%`}}></div>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="kpi-grid">
        <div className="kpi-cell"><div className="kpi-label">DAILY STREAK</div><div className="kpi-value">{stats.dailyStreak}</div><div className="kpi-note">trading days</div></div>
        <div className="kpi-cell"><div className="kpi-label">UNIQUE DAYS</div><div className="kpi-value">{stats.uniqueDays}</div><div className="kpi-note">days logged</div></div>
        <div className="kpi-cell"><div className="kpi-label">LONGEST WIN STREAK</div><div className="kpi-value" style={{color:'var(--green)'}}>{stats.longestWinStreak}</div><div className="kpi-note">consecutive wins</div></div>
        <div className="kpi-cell"><div className="kpi-label">TOTAL R</div><div className="kpi-value" style={{color: stats.totalR >= 0 ? 'var(--green)' : 'var(--red)'}}>{stats.totalR > 0 ? '+' : ''}{stats.totalR.toFixed(1)}R</div></div>
      </div>

      {/* BADGES BY TIER */}
      {tiers.map(tier => {
        const tierBadges = BADGES.filter(b => b.tier === tier);
        const tierUnlocked = tierBadges.filter(b => unlockedIds.has(b.id)).length;
        return (
          <div key={tier} className="card mb-24">
            <div className="flex-between mb-16">
              <div className="card-title" style={{marginBottom:0}}>{TIER_NAMES[tier].toUpperCase()}</div>
              <span style={{fontSize:12,color:'var(--text-3)',fontFamily:'DM Mono,monospace'}}>
                {tierUnlocked} / {tierBadges.length}
              </span>
            </div>
            <div className="badges-grid">
              {tierBadges.map(b => {
                const unlocked = unlockedIds.has(b.id);
                return (
                  <div key={b.id} className={`badge-card ${unlocked ? 'unlocked' : 'locked'}`}>
                    <div className="badge-icon">{b.icon}</div>
                    <div className="badge-title">{b.title}</div>
                    <div className="badge-desc">{b.desc}</div>
                    {unlocked && <div className="badge-unlocked-label">✓ UNLOCKED</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
